/**
 * Axis-alignment solvers for wall-sketch edges: orthogonalize (snap near-90°
 * corners to exactly 90°) and parallelize (align walls onto one common
 * direction), both moving nodes as little as possible.
 *
 * The user selects a set of edges; each selected edge whose direction is
 * close to a target axis — the sketch's dominant 90° grid for orthogonalize,
 * the single dominant direction for parallelize — is constrained to that
 * axis. Node positions are then solved so every constrained edge lies on
 * its target axis while endpoints stay near their originals.
 *
 * The grid is NOT world-aligned: buildings are often rotated, so the reference
 * orientation is derived from the selected edges themselves (circular mean of
 * their angles modulo 90°). "Axis-aligned" therefore means aligned to that
 * dominant orientation and its perpendicular.
 *
 * Solver strategy (dependency-free, minimal-movement): each constrained edge is
 * rotated about its MIDPOINT onto its target axis. Shared nodes couple the
 * edges, so this is iterated (Gauss–Seidel style) until positions converge.
 * Rotating about the midpoint keeps the edge's centre fixed, which minimises
 * middlepoint deviation from the original edges. Nodes that also touch
 * non-selected edges are anchored so the rest of the sketch is left untouched.
 */

export interface OrthoPoint {
  x: number;
  y: number;
}

export interface OrthoEdge {
  id: string;
  startNodeId: string;
  endNodeId: string;
}

export interface OrthogonalizeInput {
  /** All nodes of the sketch, by id (positions in absolute world XY). */
  nodes: Map<string, OrthoPoint>;
  /** The edges the user selected to orthogonalize. */
  selectedEdges: OrthoEdge[];
  /** Ids of nodes that must not move (touch non-selected edges). */
  anchoredNodeIds: ReadonlySet<string>;
  /** Max angular deviation from an axis for an edge to be snapped, in degrees. */
  toleranceDeg?: number;
  /** Max solver iterations. */
  maxIterations?: number;
}

export interface OrthogonalizeResult {
  /** New XY positions for the nodes that moved, by node id. */
  movedNodes: Map<string, OrthoPoint>;
  /** Number of edges that were actually constrained (near an axis). */
  constrainedEdgeCount: number;
}

const DEG = Math.PI / 180;

/**
 * Circular mean of edge orientations with the given period (π/2 for the 90°
 * grid, π for a single parallel direction). Weighted by edge length so long
 * walls dominate. Returns the reference angle in radians, in [0, period).
 */
function referenceOrientation(nodes: Map<string, OrthoPoint>, edges: OrthoEdge[], period: number): number {
  // Folding the angle into [0, period) and multiplying by `fold` maps one
  // period onto a full turn, so the ordinary circular mean applies.
  const fold = (2 * Math.PI) / period;
  let sx = 0;
  let sy = 0;
  for (const e of edges) {
    const a = nodes.get(e.startNodeId);
    const b = nodes.get(e.endNodeId);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    let folded = Math.atan2(dy, dx) % period;
    if (folded < 0) folded += period;
    sx += len * Math.cos(fold * folded);
    sy += len * Math.sin(fold * folded);
  }
  if (sx === 0 && sy === 0) return 0;
  let ref = Math.atan2(sy, sx) / fold;
  if (ref < 0) ref += period;
  return ref;
}

/** One edge pinned to a target axis direction. */
interface AxisConstraint {
  a: string;
  b: string;
  axis: number;
  axisY: number;
}

/**
 * Build the axis constraints for `selectedEdges`: each edge targets the
 * nearest multiple of `period` off the reference orientation, and is kept
 * only when its current direction deviates less than `toleranceDeg`.
 */
function buildAxisConstraints(nodes: Map<string, OrthoPoint>, selectedEdges: OrthoEdge[], period: number, toleranceDeg: number): AxisConstraint[] {
  const ref = referenceOrientation(nodes, selectedEdges, period);
  const constraints: AxisConstraint[] = [];
  for (const e of selectedEdges) {
    const pa = nodes.get(e.startNodeId);
    const pb = nodes.get(e.endNodeId);
    if (!pa || !pb) continue;
    const dx = pb.x - pa.x;
    const dy = pb.y - pa.y;
    if (Math.hypot(dx, dy) < 1e-9) continue;

    const theta = Math.atan2(dy, dx);
    const k = Math.round((theta - ref) / period);
    const target = ref + k * period;
    // Angular deviation, wrapped to [-π, π].
    let dev = theta - target;
    while (dev > Math.PI) dev -= 2 * Math.PI;
    while (dev < -Math.PI) dev += 2 * Math.PI;
    if (Math.abs(dev) > toleranceDeg * DEG) continue;

    constraints.push({ a: e.startNodeId, b: e.endNodeId, axis: Math.cos(target), axisY: Math.sin(target) });
  }
  return constraints;
}

/**
 * Solve node positions so near-axis selected edges snap onto the grid.
 * Returns only the nodes that actually moved.
 */
export function orthogonalizeEdges(input: OrthogonalizeInput): OrthogonalizeResult {
  const constraints = buildAxisConstraints(input.nodes, input.selectedEdges, Math.PI / 2, input.toleranceDeg ?? 20);
  return solveAxisConstraints(input.nodes, constraints, input.anchoredNodeIds, input.maxIterations ?? 200);
}

/**
 * Make the selected edges PARALLEL: every edge within tolerance of the
 * selection's dominant direction (circular mean with period 180°, so
 * anti-parallel walls count as parallel) is aligned onto it, with the same
 * minimal-movement midpoint-pivot solve as {@link orthogonalizeEdges}. The
 * default tolerance is wide — an explicit "make parallel" should catch every
 * plausibly-parallel wall, excluding only near-perpendicular ones where the
 * result would surprise.
 */
export function parallelizeEdges(input: OrthogonalizeInput): OrthogonalizeResult {
  const constraints = buildAxisConstraints(input.nodes, input.selectedEdges, Math.PI, input.toleranceDeg ?? 45);
  return solveAxisConstraints(input.nodes, constraints, input.anchoredNodeIds, input.maxIterations ?? 200);
}

/**
 * Iteratively project each constrained edge onto its axis (Gauss–Seidel; see
 * module header). Returns the moved nodes + how many edges were constrained.
 */
function solveAxisConstraints(nodes: Map<string, OrthoPoint>, constraints: AxisConstraint[], anchoredNodeIds: ReadonlySet<string>, maxIterations: number): OrthogonalizeResult {
  // Working copy of positions for nodes involved in constraints.
  const work = new Map<string, OrthoPoint>();
  for (const c of constraints) {
    if (!work.has(c.a)) {
      const p = nodes.get(c.a);
      if (p) work.set(c.a, { x: p.x, y: p.y });
    }
    if (!work.has(c.b)) {
      const p = nodes.get(c.b);
      if (p) work.set(c.b, { x: p.x, y: p.y });
    }
  }

  // Iteratively project each edge onto its axis. For a constrained edge, keep
  // the component along the axis but zero the perpendicular component relative
  // to the edge midpoint, distributing the correction between the two endpoints
  // (all to the free one when the other is anchored).
  for (let iter = 0; iter < maxIterations; iter++) {
    let maxShift = 0;
    for (const c of constraints) {
      const pa = work.get(c.a);
      const pb = work.get(c.b);
      if (!pa || !pb) continue;
      const aAnchored = anchoredNodeIds.has(c.a);
      const bAnchored = anchoredNodeIds.has(c.b);
      if (aAnchored && bAnchored) continue; // nothing we can move

      const ux = c.axis;
      const uy = c.axisY;
      // Perpendicular axis.
      const nx = -uy;
      const ny = ux;
      // Signed perpendicular offset of each endpoint from the axis line through
      // the midpoint.
      const mx = (pa.x + pb.x) / 2;
      const my = (pa.y + pb.y) / 2;
      const perpA = (pa.x - mx) * nx + (pa.y - my) * ny;
      const perpB = (pb.x - mx) * nx + (pb.y - my) * ny;

      if (!aAnchored && !bAnchored) {
        // Move both endpoints onto the axis symmetrically (midpoint fixed).
        const shiftAx = -perpA * nx;
        const shiftAy = -perpA * ny;
        const shiftBx = -perpB * nx;
        const shiftBy = -perpB * ny;
        pa.x += shiftAx;
        pa.y += shiftAy;
        pb.x += shiftBx;
        pb.y += shiftBy;
        maxShift = Math.max(maxShift, Math.hypot(shiftAx, shiftAy), Math.hypot(shiftBx, shiftBy));
      } else if (aAnchored) {
        // Rotate b about the anchored a onto the axis: project (b-a) onto axis.
        const vx = pb.x - pa.x;
        const vy = pb.y - pa.y;
        const along = vx * ux + vy * uy;
        const tx = pa.x + along * ux;
        const ty = pa.y + along * uy;
        maxShift = Math.max(maxShift, Math.hypot(tx - pb.x, ty - pb.y));
        pb.x = tx;
        pb.y = ty;
      } else {
        // b anchored: rotate a about b.
        const vx = pa.x - pb.x;
        const vy = pa.y - pb.y;
        const along = vx * ux + vy * uy;
        const tx = pb.x + along * ux;
        const ty = pb.y + along * uy;
        maxShift = Math.max(maxShift, Math.hypot(tx - pa.x, ty - pa.y));
        pa.x = tx;
        pa.y = ty;
      }
    }
    if (maxShift < 1e-6) break;
  }

  // Collect nodes that actually moved (skip anchored + unchanged).
  const movedNodes = new Map<string, OrthoPoint>();
  for (const [id, p] of work) {
    if (anchoredNodeIds.has(id)) continue;
    const orig = nodes.get(id);
    if (!orig) continue;
    if (Math.hypot(p.x - orig.x, p.y - orig.y) > 1e-7) {
      movedNodes.set(id, { x: p.x, y: p.y });
    }
  }

  return { movedNodes, constrainedEdgeCount: constraints.length };
}
